import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Dialog,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Tooltip,
  Tab,
  TextField,
  Fade,
  Select,
  Box,
  Switch,
  FormControlLabel,
} from '@material-ui/core'
import { Lock, LockOpen, FileCopy } from '@material-ui/icons'
import { withRouter } from 'react-router-dom'
import DeviceModalTemplate from 'components/templates/DeviceModalTemplate'
import { connect, useSelector } from 'react-redux'
import {
  Button,
  Tabs,
  Loader,
  useInput,
  formatDeviceId,
  Container,
} from 'sputnik-ui'
import styled from 'styled-components'
import { copyToClipboard, formatDate } from 'utils/helpers'
import trackGoal, {
  trackIntercomRebootFailed,
  trackIntercomRebootMetrics,
} from 'utils/metrika'
import { useTranslation } from 'react-i18next'
import { updateOsd } from 'features/Devices/devicesSlice'

import OnlineIcon from 'components/atoms/OnlineIcon'
import FullscreenExitButton from 'components/atoms/FullscreenExitButton'
import FullscreenDialogTitle from 'components/atoms/FullscreenDialogTitle'

import OpenTimeSelect from 'components/atoms/OpenTimeSelect'
import CommutatorScheme from 'components/atoms/CommutatorScheme'
import ConfirmActionModal from 'components/organisms/ConfirmActionModal'

import {
  getCamera,
  fetchIntercomByUuid,
  openDoor,
  rebootIntercom,
  unlinkDevice,
  updateDeviceSettings,
  fetchSoundPresets,
  updateFirmware,
} from 'store/devices/operations'
import {
  clearCurrentDevice,
  updateCurrentIntercomStatus,
  updateCurrentIntercomDoorStatus,
} from 'store/devices/actions'
import { fetchGate } from 'store/gates/operations'
import useIntercomStatus from 'hooks/useIntercomStatus'
import { showInfoMessage } from 'store/ui/actions'
import { fixStreamCamera } from 'features/Cameras/camerasSlice'
import { tmzOptions, tmzParse } from 'components/atoms/tmzOptions'
import { isEltex } from 'utils/thirdPartyIntercomKinds'
import { isInAppNavigation, deviceAddressPath } from 'utils/deviceNavigation'
import { EditThirdPartyIntercom } from 'features/Devices/EditThirdPartyIntercom'
import { getThirdPartyIntercom } from 'features/Intercoms/intercomsSlice'
import { useTestingPanel } from './TestingPanel'
import ControlCollectKeys from '../ControlCollectKeys'
import HouseRect from './HouseRect'
import { ConnectSecondDoorModal } from './ConnectSecondDoorModal'
import { ExtraKeyReader } from './ExtraKeyReader'
import { OpenSecondDoorButton } from './OpenSecondDoorButton'
import { FirmwareVersionTab, DynamicKeysTab } from './Tabs'

const DeviceModal = ({
  match,
  history,
  dispatch,
  camera,
  device,
  devicesError,
  gate,
  loading,
  soundPresets,
}) => {
  const {
    params: { device: deviceUuid, gate: gateAddress },
  } = match
  const gateUuid = gate?.intercom_uuid
  const uuidOfCurrentDevice = deviceUuid || gateUuid

  // Контроллер Eltex: тот же шаблон карточки, но без секций домофона (звук, SIP,
  // коммутатор, квартиры, камера). Подключение и режим ключей приходят из
  // /foreign_intercoms — их подгружает EditThirdPartyIntercom, смонтированный ниже.
  const isEltexDevice = isEltex(device?.model)
  const foreignIntercom = useSelector(({ intercoms }) => intercoms.current)
  const [editConnectionModal, setEditConnectionModal] = useState(false)
  const handleCloseEditConnection = () => {
    setEditConnectionModal(false)
    dispatch(getThirdPartyIntercom(uuidOfCurrentDevice))
  }

  const handleUpdateOsd = () => {
    dispatch(updateOsd(uuidOfCurrentDevice))
  }

  const { error: gateFetchError } = useSelector((state) => state.gates)
  const authUser = useSelector((state) => state.auth?.user)
  const handleClose = useCallback(() => {
    if (!device) return
    if (isInAppNavigation(history.action)) history.goBack()
    else history.push(deviceAddressPath(device))
  }, [device, history])
  const [confirmUnlinkModal, setConfirmUnlinkModal] = useState(false)
  const [secondDoorModal, setSecondDoorModal] = useState(false)

  const handleUnlinkDevice = () => {
    if (device) {
      const unlinkUuid =
        device?.address_type === 'gate'
          ? gate?.address_uuid
          : device?.address_uuid

      dispatch(unlinkDevice(uuidOfCurrentDevice, unlinkUuid)).then(() => {
        history.push(
          `/${device?.address_type === 'gate' ? 'h' : 'e'}/${unlinkUuid}`
        )
      })
    }
  }

  useEffect(() => {
    if (gateFetchError?.status === 404) {
      history.push(`/`)
    }
  }, [gateFetchError, history])
  useEffect(() => {
    if (deviceUuid) {
      dispatch(getCamera(deviceUuid))
      dispatch(fetchIntercomByUuid(deviceUuid))
    } else {
      dispatch(fetchGate(gateAddress)).then((gate) => {
        dispatch(getCamera(gate.intercom_uuid))
        dispatch(fetchIntercomByUuid(gate.intercom_uuid))
        // TODO reserch
      })
    }

    dispatch(fetchSoundPresets())

    return () => dispatch(clearCurrentDevice())
  }, [deviceUuid, dispatch, gateAddress, gateUuid])

  const onMsgReceived = useCallback(
    (msg) => {
      if ('is_online' in msg) {
        dispatch(updateCurrentIntercomStatus(msg.is_online === 'true'))
      } else if ('door_opened' in msg) {
        dispatch(updateCurrentIntercomDoorStatus(msg.door_opened))
      }
    },
    [dispatch]
  )
  useIntercomStatus(deviceUuid || gate?.intercom_uuid, onMsgReceived)

  // settings
  const {
    value: extCom,
    bind: bindExtCom,
    setValue: setExtCom,
  } = useInput(comOptionsForV2[0])

  const {
    value: tmzConfig,
    bind: bindTmz,
    setValue: setTmz,
  } = useInput(tmzOptions[5])

  const NoNcOptions = [
    { value: 'nc', label: 'NC' },
    { value: 'no', label: 'NO' },
  ]
  const {
    value: noNcGeneral,
    bind: bindNoNcGeneral,
    setValue: setNoNcGeneral,
  } = useInput('nc')

  const {
    value: noNcSecond,
    bind: bindNoNcSecond,
    setValue: setNoNcSecond,
  } = useInput('nc')

  const [wiegand, setWiegand] = useState(false)

  const {
    value: generalVol,
    bind: bindGeneralVol,
    setValue: setGeneralVol,
  } = useInput(90, () => {
    trackGoal('control_device_soundConfig')
    trackGoal('control_device_soundConfigGeneral', { value: generalVol })
  })

  const {
    value: speakHandsetTx,
    bind: bindSpeakHandsetTx,
    setValue: setSpeakHandsetTx,
  } = useInput(65, () => {
    trackGoal('control_device_soundConfig')
    trackGoal('control_device_soundConfigMic', { value: speakHandsetTx })
  })

  const {
    value: flatOffset,
    bind: bindOffsetInput,
    setValue: setOffset,
  } = useInput(0)

  const {
    value: intercomSounds,
    bind: bindIntercomSounds,
    setValue: setIntercomSounds,
  } = useInput('sputnik')

  const {
    value: openTimeGeneral,
    bind: bindOpenTimeGeneral,
    setValue: setOpenTimeGeneral,
  } = useInput(0)

  const {
    value: speakLoudspeaker,
    bind: bindSpeakLoudspeaker,
    setValue: setSpeakLoudspeaker,
  } = useInput(0)

  useEffect(() => {
    if (device?.config) {
      setOpenTimeGeneral(device.config.doors_open_durations.general)
      setExtCom(device.config.ext_com)
      setGeneralVol(device.config.sound_config.general)
      setSpeakHandsetTx(device.config.sound_config.speakHandsetTx)
      setSpeakLoudspeaker(device.config.sound_config.speakLoudspeaker)
      setIntercomSounds(device.config.current_preset)
      setOffset(device.config.flat_offset)
      setTmz(tmzParse(device.config.tmz))
      setNoNcSecond(device.extra_key_reader?.normally_open)
      setNoNcGeneral(device.config?.normally_open)
      setWiegand(device.wiegand)
    }
  }, [
    device,
    setExtCom,
    setGeneralVol,
    setIntercomSounds,
    setSpeakHandsetTx,
    setOpenTimeGeneral,
    setSpeakLoudspeaker,
    setOffset,
    setTmz,
    setNoNcGeneral,
    setNoNcSecond,
    setWiegand,
  ])

  const parsedV1extComFromV2 = Math.max(
    comOptionsForV2.findIndex((el) => el === extCom),
    0
  )

  const checkIfChanged = (value, origValue) => {
    return value === origValue ? null : value
  }
  const handleUpdate = useCallback(() => {
    if (!device) return
    const extComChecked = checkIfChanged(extCom, device.config.ext_com)
      ? parsedV1extComFromV2
      : null
    const soundChanges = () => {
      if (
        checkIfChanged(generalVol, device.config.sound_config.general) ||
        checkIfChanged(
          speakHandsetTx,
          device.config.sound_config.speakHandsetTx
        ) ||
        checkIfChanged(
          speakLoudspeaker,
          device.config.sound_config.speakLoudspeaker
        )
      ) {
        return {
          general: generalVol,
          speak_handset_tx: speakHandsetTx,
          speak_loudspeaker: speakLoudspeaker,
        }
      }
      return {}
    }

    const params = {
      ...{
        ext_com: extComChecked,
        ...soundChanges,
        flat_offset: checkIfChanged(flatOffset, device.config.flat_offset),
        preset_name: checkIfChanged(
          intercomSounds,
          device.config.current_preset
        ),
        door_open_time_general: checkIfChanged(
          openTimeGeneral,
          device.config.doors_open_durations.general
        ),
        tmz: checkIfChanged(tmzConfig, tmzParse(device.config.tmz)),
        wiegand: checkIfChanged(wiegand, device.wiegand),
        extra_key_reader: checkIfChanged(
          { normally_open: noNcSecond },
          { normally_open: device.extra_key_reader?.normally_open }
        ),
        normally_open: checkIfChanged(
          noNcGeneral,
          device.config?.normally_open
        ),
      },
      ...soundChanges(),
    }
    dispatch(
      updateDeviceSettings(uuidOfCurrentDevice, params, device.is_online)
    )
  }, [
    device,
    dispatch,
    uuidOfCurrentDevice,
    parsedV1extComFromV2,
    generalVol,
    speakHandsetTx,
    flatOffset,
    intercomSounds,
    openTimeGeneral,
    speakLoudspeaker,
    tmzConfig,
    wiegand,
    noNcGeneral,
    noNcSecond,
    extCom,
  ])

  const [currentTab, setCurrentTab] = React.useState('soundSettings')
  const [selectedFirmwareUuid, setSelectedFirmwareUuid] = useState('')

  const { testingTabs, testingChildren } = useTestingPanel({
    uuidOfCurrentDevice,
    dispatch,
    loading,
  })

  const handleUpdateFirmware = () => {
    if (!selectedFirmwareUuid) return
    const selectedFirmware = device?.available_sw_versions?.find(
      (firmware) => firmware.uuid === selectedFirmwareUuid
    )
    trackGoal('swUpdated')
    dispatch(
      updateFirmware({
        intercomUuid: uuidOfCurrentDevice,
        firmwareUuid: selectedFirmwareUuid,
        firmwareTag: selectedFirmware?.tag,
        firmwareVersion: selectedFirmware?.version,
      })
    )
  }

  // open time
  const [openTime, setOpenTime] = useState(5)

  const { t } = useTranslation([
    'devices',
    'common',
    'messages',
    'info',
    'geo',
    'people',
  ])

  const commutatorOptions = Array.from({ length: 7 }, (_, num) => ({
    value: num,
    label: t('devices:params.com_interval', {
      count: num,
      postProcess: 'interval',
    }),
  }))
  const housesRef = useRef(null)

  const handleCloseSecondDoorModal = () => {
    setSecondDoorModal(false)
  }

  const handleOpenSecondDoorModal = () => {
    setSecondDoorModal(true)
  }

  const handleRebootIntercom = () => {
    const companyUuid = authUser?.company?.uuid
    const userId = authUser?.id

    dispatch(rebootIntercom(uuidOfCurrentDevice))
      .then(() => {
        trackIntercomRebootMetrics({
          userId,
          companyUuid,
          intercomUuid: uuidOfCurrentDevice,
        })
      })
      .catch((errorMessage) => {
        trackIntercomRebootFailed({
          userId,
          companyUuid,
          intercomUuid: uuidOfCurrentDevice,
          errorMessage,
        })
      })
  }

  return (
    <Dialog
      TransitionComponent={Transition}
      transitionDuration={0}
      fullScreen
      open
      onClose={handleClose}
    >
      {!device ? (
        <Loader error={devicesError} />
      ) : (
        <DeviceModalTemplate
          device={device}
          header={
            <>
              <FullscreenExitButton variant="back" onClick={handleClose} />
              <Container
                style={{
                  height: 'fit-content',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <div />
                {device?.serial_number ? (
                  <Tooltip
                    title={t('devices:actions.unlink')}
                    placement="bottom"
                  >
                    <Button
                      style={{
                        top: '2.7rem',
                      }}
                      onClick={() => {
                        if (device.address_type === 'gate') {
                          housesRef.current.scrollIntoView({
                            behavior: 'smooth',
                          })
                        } else {
                          setConfirmUnlinkModal(true)
                        }
                      }}
                      variant="outlined"
                      color="danger"
                    >
                      {t('devices:actions.unlink_device')}
                    </Button>
                  </Tooltip>
                ) : null}
              </Container>
              <FullscreenDialogTitle
                style={{ marginTop: '2rem' }}
                subtitle={
                  device
                    ? device?.address_type === 'gate'
                      ? t('devices:gates.subtitle')
                      : isEltexDevice
                      ? t('devices:helper_text.controller_modal')
                      : t('devices:helper_text.device_modal')
                    : ''
                }
              >
                <>
                  <Tooltip title={device?.serial_number}>
                    <span>
                      <span>
                        {device?.address_type === 'gate'
                          ? t('devices:gates.gate_numbered')
                          : isEltexDevice
                          ? t('devices:intercoms.controller_numbered')
                          : t('devices:intercoms.sputnik_numbered')}
                        {formatDeviceId(device?.serial_number)}
                      </span>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      device.is_online
                        ? t('devices:params.online')
                        : t('devices:params.offline')
                    }
                  >
                    <span>
                      <OnlineIcon
                        isOnline={device && device.is_online}
                        style={{ marginLeft: '.325rem', width: 14, height: 14 }}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title={t('common:button.copy')}>
                    <FileCopy
                      style={{
                        width: 14,
                        height: 14,
                        marginLeft: '.325rem',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        copyToClipboard(device?.serial_number)
                        dispatch(
                          showInfoMessage(
                            t('info:copy.device', {
                              number: device?.serial_number,
                            })
                          )
                        )
                      }}
                    />
                  </Tooltip>
                </>
              </FullscreenDialogTitle>
            </>
          }
          camera={isEltexDevice ? null : camera}
          hideCamera={isEltexDevice}
          subCamera={
            <>
              <Box
                display="flex"
                // alignItems="center"
                justifyContent="space-between"
                width="100%"
                flexWrap="wrap"
                style={{ gap: '.5rem' }}
              >
                <Box
                  display="flex"
                  alignItems="start"
                  flexDirection="column"
                  style={{ gap: '.5rem' }}
                >
                  {device?.address_type !== 'gate' && !isEltexDevice ? (
                    <ControlCollectKeys />
                  ) : null}
                  {!isEltexDevice && (
                    <Button
                      rounded
                      secondary
                      onClick={() => dispatch(fixStreamCamera(camera.uuid))}
                    >
                      {t('devices:params.stream_sync')}
                    </Button>
                  )}
                  <Button
                    rounded
                    secondary
                    disabled={loading}
                    onClick={handleRebootIntercom}
                  >
                    {t('devices:actions.reboot_intercom')}
                  </Button>

                  <Button
                    rounded
                    secondary
                    onClick={handleOpenSecondDoorModal}
                    disabled={!!device?.extra_key_reader}
                  >
                    {t('devices:actions.connect_sec_door')}
                  </Button>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  style={{ gap: '.5rem' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: '1rem' }}
                  >
                    {!isEltexDevice && (
                      <OpenTimeSelect value={openTime} onChange={setOpenTime} />
                    )}
                    <Button
                      disabled={
                        loading || device.door_opened || !device.is_online
                      }
                      variant="contained"
                      color="primary"
                      rounded
                      onClick={() =>
                        dispatch(openDoor(uuidOfCurrentDevice, openTime))
                      }
                    >
                      {device.door_opened ? (
                        <>
                          <LockOpen
                            color="inherit"
                            style={{ marginRight: 8 }}
                          />
                          {t('devices:params.opened')}
                        </>
                      ) : (
                        <>
                          <Lock color="inherit" style={{ marginRight: 8 }} />
                          {t('devices:actions.open_door')}
                        </>
                      )}
                    </Button>
                  </Box>

                  {!!device?.extra_key_reader && (
                    <OpenSecondDoorButton
                      uuid={uuidOfCurrentDevice}
                      httpRelay={device?.extra_key_reader?.http_relay}
                    />
                  )}
                </Box>
              </Box>
            </>
          }
          afterCamera={
            <>
              {device?.extra_key_reader && (
                <div style={{ marginTop: '1rem' }}>
                  <ExtraKeyReader
                    currnentDeviceUuid={uuidOfCurrentDevice}
                    kind={device?.extra_key_reader?.kind}
                    alias={device?.extra_key_reader?.alias}
                  />
                </div>
              )}
              {device?.address_type === 'gate' && (
                <div style={{ marginTop: '1rem' }}>
                  <Typography ref={housesRef} variant="h5">
                    {t('geo:locations.connected_house_plural')}
                  </Typography>
                  <Typography color="textSecondary" gutterBottom>
                    {t('devices:helper_text.choose_house_to_unlink')}
                  </Typography>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, max-content))',
                      gridGap: '1rem',
                    }}
                  >
                    {gate?.houses?.length > 0 ? (
                      gate?.houses?.map((house) => (
                        <HouseRect {...house} key={house.uuid} />
                      ))
                    ) : (
                      <Typography>{t('messages:content.empty')}</Typography>
                    )}
                  </div>
                </div>
              )}
            </>
          }
          info={
            <>
              {device?.address_type === 'entry' && (
                <div>
                  <Typography color="textSecondary">
                    {t('devices:params.installation_point')}
                  </Typography>

                  <Typography
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {device && device.short_address}
                  </Typography>
                </div>
              )}

              <div style={styles.field}>
                <Typography color="textSecondary">
                  {t('devices:params.installation_date')}
                </Typography>
                <Typography style={{ display: 'flex', alignItems: 'center' }}>
                  {device &&
                    (device.installation_date
                      ? formatDate(device.installation_date)
                      : t('messages:helper.unknown'))}
                </Typography>
              </div>

              <div style={styles.field}>
                <Typography color="textSecondary">HW</Typography>
                <Typography style={{ display: 'flex', alignItems: 'center' }}>
                  {device?.config?.versions?.hwVersion || 'none'}
                </Typography>
              </div>
              <div style={styles.field}>
                <Typography color="textSecondary">SW</Typography>
                <Typography style={{ display: 'flex', alignItems: 'center' }}>
                  {device?.config?.versions?.swVersion || 'none'}
                </Typography>
              </div>

              {!isEltexDevice && (
                <>
                  <div style={styles.field}>
                    <Typography color="textSecondary">
                      {t('devices:cameras.unit')} FW
                    </Typography>
                    <Typography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {device?.camera?.fwVersion || 'none'}
                    </Typography>
                  </div>

                  <div style={styles.field}>
                    <Typography color="textSecondary">BLE</Typography>
                    <Typography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {device?.config?.versions?.bleVersion || 'none'}
                    </Typography>
                  </div>
                </>
              )}

              <div style={styles.field}>
                <Typography color="textSecondary">
                  {t('devices:params.model')}
                </Typography>
                <Typography style={{ display: 'flex', alignItems: 'center' }}>
                  {device?.model || 'none'}
                </Typography>
              </div>

              {isEltexDevice && foreignIntercom && (
                <>
                  <div style={styles.field}>
                    <Typography color="textSecondary">
                      {t('devices:params.controller_model')}
                    </Typography>
                    <Typography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {foreignIntercom.series?.toUpperCase() || 'none'}
                    </Typography>
                  </div>

                  <div style={styles.field}>
                    <Typography color="textSecondary">
                      {t('devices:params.controller_address')}
                    </Typography>
                    <Typography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {foreignIntercom.ip_address || 'none'}
                    </Typography>
                  </div>

                  <div style={styles.field}>
                    <Typography color="textSecondary">
                      {t('devices:params.keys_mode')}
                    </Typography>
                    <Typography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {t(
                        `devices:keys_mode.${
                          foreignIntercom.keys_mode || 'managed'
                        }`
                      )}
                    </Typography>
                  </div>
                </>
              )}
            </>
          }
          tabs={
            isEltexDevice ? null : (
              <Tabs
                onChange={(e, tab) => setCurrentTab(tab)}
                value={currentTab}
                variant="scrollable"
                scrollButtons="auto"
              >
                {tabs.map((tab) => (
                  <Tab
                    disableRipple
                    label={t(`devices:params.${tab.label}`)}
                    value={tab.value}
                    key={tab.value}
                    style={{ whiteSpace: 'nowrap' }}
                  />
                ))}
              </Tabs>
            )
          }
          testingTitle={t('devices:params.testing')}
          testingTabs={isEltexDevice ? undefined : testingTabs}
          testingChildren={isEltexDevice ? undefined : testingChildren}
        >
          <form style={styles.configForm}>
            {isEltexDevice && (
              <>
                <Typography color="textSecondary" style={styles.field}>
                  {t('devices:helper_text.controller_settings_note')}
                </Typography>
                <Button
                  secondary
                  variant="contained"
                  rounded
                  style={{ marginLeft: 'auto', marginTop: '1rem' }}
                  onClick={() => setEditConnectionModal(true)}
                >
                  {t('devices:intercoms.third_party.edit_connection')}
                </Button>
                <EditThirdPartyIntercom
                  open={editConnectionModal}
                  onClose={handleCloseEditConnection}
                  uuid={uuidOfCurrentDevice}
                />
              </>
            )}

            {!isEltexDevice && currentTab === 'soundSettings' && (
              <>
                <Row style={styles.field}>
                  <TextField
                    label={t('devices:params.volume')}
                    inputProps={{
                      type: 'number',
                      min: 1,
                      max: 100,
                    }}
                    placeholder="85"
                    helperText={`${t('messages:helper.recommended')}: 85`}
                    style={styles.field}
                    {...bindGeneralVol}
                  />
                  <TextField
                    label={t('devices:params.mic')}
                    inputProps={{
                      type: 'number',
                      min: 1,
                      max: 100,
                    }}
                    {...bindSpeakHandsetTx}
                    placeholder="65"
                    style={styles.field}
                    helperText={`${t('messages:helper.recommended')}: 65`}
                  />
                </Row>
                <TextField
                  label={t('devices:params.load_speaker')}
                  inputProps={{
                    type: 'number',
                    min: 1,
                    max: 100,
                  }}
                  {...bindSpeakLoudspeaker}
                  placeholder="65"
                  style={styles.field}
                  helperText={`${t('messages:helper.recommended')}: 85`}
                />
                <FormControl style={styles.field}>
                  <InputLabel htmlFor="sound_presets">
                    {t('devices:params.sound_preset')}
                  </InputLabel>
                  <Select
                    {...bindIntercomSounds}
                    inputProps={{
                      name: 'sound_presets',
                      id: 'sound_presets',
                    }}
                  >
                    {soundPresets &&
                      soundPresets.map(({ name, locale_name }) => (
                        <MenuItem value={name} key={name}>
                          {locale_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </>
            )}

            {!isEltexDevice && currentTab === 'commutatorSettings' && (
              <>
                <FormControl style={styles.field}>
                  <InputLabel htmlFor="ext_com">
                    {t('devices:params.com_type')}
                  </InputLabel>
                  <Select
                    {...bindExtCom}
                    inputProps={{
                      name: 'ext_com',
                      id: 'ext_com',
                    }}
                  >
                    {commutatorOptions.map(({ value, label }, idx) => (
                      <MenuItem value={comOptionsForV2[value]} key={value}>
                        {t('devices:params.com_interval', {
                          count: idx,
                          postProcess: 'interval',
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={t('devices:params.flat_offset')}
                  type="number"
                  style={styles.field}
                  inputProps={{
                    type: 'number',
                    min: -1000,
                    max: 1000,
                  }}
                  {...bindOffsetInput}
                  helperText={
                    <a href="https://help.sputnik.systems/ru/articles/3637638-%D1%81%D0%BC%D0%B5%D1%89%D0%B5%D0%BD%D0%B8%D0%B5-%D0%B4%D0%B8%D0%B0%D0%BF%D0%B0%D0%B7%D0%BE%D0%BD%D0%B0">
                      {t('common:what_is')}
                    </a>
                  }
                />

                <CommutatorScheme commutator={parsedV1extComFromV2} />
              </>
            )}

            {!isEltexDevice && currentTab === 'door_settings' && (
              <>
                <TextField
                  label={t('devices:params.doorSettings')}
                  type="number"
                  style={styles.field}
                  {...bindOpenTimeGeneral}
                  inputProps={{
                    type: 'number',
                    min: 1,
                    max: 65535,
                  }}
                />
                <FormControl style={styles.field}>
                  <InputLabel htmlFor="ext_com">
                    {t('devices:params.choose_no_nc_general')}
                  </InputLabel>

                  <Select {...bindNoNcGeneral}>
                    {NoNcOptions.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {t(`devices:helper_text.${label}_explain`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  style={styles.field}
                  disabled={!device?.extra_key_reader}
                >
                  <InputLabel htmlFor="ext_com">
                    {t('devices:params.choose_no_nc_second')}
                  </InputLabel>

                  <Select {...bindNoNcSecond}>
                    {NoNcOptions.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {t(`devices:helper_text.${label}_explain`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  style={{ marginTop: 10, display: 'flex' }}
                  control={
                    <Switch
                      checked={wiegand}
                      onChange={(e) => setWiegand(e.target.checked)}
                    />
                  }
                  label={
                    <InputLabel htmlFor="ext_com">
                      {t('devices:params.wiegand')}
                    </InputLabel>
                  }
                  labelPlacement="left"
                />
              </>
            )}

            {!isEltexDevice && currentTab === 'camera_settings' && (
              <>
                <FormControl style={styles.field}>
                  <InputLabel htmlFor="ext_com">
                    {t('devices:params.choose_tmz')}
                  </InputLabel>
                  <Select {...bindTmz}>
                    {tmzOptions.map(({ value, label }, idx) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  secondary
                  variant="contained"
                  rounded
                  style={{ marginLeft: 'auto', marginTop: '1rem' }}
                  onClick={handleUpdateOsd}
                >
                  {t('devices:params.update_osd')}
                </Button>
              </>
            )}

            {!isEltexDevice && currentTab === 'firmware_versions' && (
              <FirmwareVersionTab
                device={device}
                selectedFirmwareUuid={selectedFirmwareUuid}
                styles={styles}
                setSelectedFirmwareUuid={setSelectedFirmwareUuid}
                handleUpdateFirmware={handleUpdateFirmware}
                loading={loading}
                t={t}
              />
            )}

            {!isEltexDevice && currentTab === 'dynamic_keys' && (
              <DynamicKeysTab
                dynamic_keys={device.dynamic_keys}
                t={t}
                styles={styles}
              />
            )}

            {!isEltexDevice && currentTab !== 'firmware_versions' && (
              <Button
                primary
                variant="contained"
                rounded
                style={{ marginLeft: 'auto', marginTop: '1rem' }}
                onClick={handleUpdate}
                disabled={loading}
              >
                {t('common:button.update_settings')}
                {loading && '...'}
              </Button>
            )}
          </form>

          <ConfirmActionModal
            open={confirmUnlinkModal}
            action={t('devices:actions.unlink')}
            handleAction={handleUnlinkDevice}
            handleClose={() => setConfirmUnlinkModal(false)}
          />

          <ConnectSecondDoorModal
            currnentDeviceUuid={uuidOfCurrentDevice}
            serial={device?.serial_number}
            open={secondDoorModal}
            onClose={handleCloseSecondDoorModal}
          />
        </DeviceModalTemplate>
      )}
    </Dialog>
  )
}

const mapStateToProps = ({ devices, gates }) => ({
  camera: devices.camera,
  device: devices.intercom || {},
  devicesError: devices.error,
  loading: devices.loading,
  soundPresets: devices.soundPresets,
  gate: gates.current,
})

const comOptionsForV2 = [
  'METACOM',
  'VIZIT',
  'CYFRAL',
  'ELTIS',
  'DAXIS',
  'BEWARD',
  'COM80',
]

const Row = styled.div`
  display: flex;
  margin-bottom: 1rem;

  & > * {
    flex: 1;

    &:not(:last-child) {
      margin-right: 1rem;
    }
  }

  @media screen and (max-width: ${(p) => p.theme.sizes.tablet}) {
    flex-direction: column;
    & > * {
      margin-right: 0;
    }
  }
`

const styles = {
  field: {
    marginBottom: '.625rem',
  },
  configForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  iconField: {
    marginRight: '.625rem',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  verField: {
    display: 'flex',
    height: '51.43px',
    // height px иначе iconFiled hight 100% не отрабатывает
    marginBottom: '.625rem',
  },
}

const tabs = [
  {
    label: 'sound_settings',
    value: 'soundSettings',
  },
  {
    label: 'com_settings',
    value: 'commutatorSettings',
  },
  {
    label: 'door_settings',
    value: 'door_settings',
  },
  {
    label: 'camera_settings',
    value: 'camera_settings',
  },
  {
    label: 'versions',
    value: 'firmware_versions',
  },

  {
    label: 'dynamic_key',
    value: 'dynamic_keys',
  },
]

function Transition(props) {
  return <Fade direction="up" {...props} />
}

export default connect(mapStateToProps)(withRouter(DeviceModal))
